import { useEffect, useState } from "react";

import { fetchGameHistory, fetchGameHistoryDetail } from "./api";
import { navigate } from "./navigation";
import { subscribeScoreInvalidations } from "./score";
import type { GameHistoryDetail, GameHistoryGame, GameHistoryItem } from "./types";

const dateFormatter = new Intl.DateTimeFormat("fr-FR", {
  dateStyle: "medium",
  timeStyle: "short",
});

function HistoryDetail({ item, onClose }: { item: GameHistoryItem; onClose: () => void }) {
  const [detail, setDetail] = useState<GameHistoryDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => subscribeScoreInvalidations(() => setRevision((value) => value + 1)), []);

  useEffect(() => {
    let active = true;
    void fetchGameHistoryDetail(item.token)
      .then((payload) => { if (active) setDetail(payload); })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : "Détail indisponible.");
      });
    return () => { active = false; };
  }, [item.token, revision]);

  return (
    <div className="history-detail" aria-live="polite">
      <div className="history-detail-heading">
        <div>
          <p className="eyebrow">Détail de la partie</p>
          <h2>{item.collection?.title ?? "Série supprimée"} · {item.game_label}</h2>
        </div>
        <button className="button" type="button" onClick={onClose}>Fermer</button>
      </div>
      {error ? <p className="game-error">{error}</p> : !detail ? (
        <p>Chargement des réponses…</p>
      ) : detail.played_cards.length ? (
        <ol className="history-attempts">
          {detail.played_cards.map((attempt) => (
            <li className={attempt.failed ? "failed" : "success"} key={attempt.id}>
              <div>
                <strong>{attempt.question ?? `Carte ${attempt.position + 1}`}</strong>
                <small>{dateFormatter.format(new Date(attempt.played_at))}</small>
              </div>
              <span>{attempt.failed ? "À revoir" : "Réussie"}</span>
              <small>Maîtrise {attempt.mastery.from} → {attempt.mastery.to}</small>
            </li>
          ))}
        </ol>
      ) : <p>Aucune réponse n’a encore été enregistrée pour cette session.</p>}
    </div>
  );
}

export function HistoryPage() {
  const [sessions, setSessions] = useState<GameHistoryItem[] | null>(null);
  const [games, setGames] = useState<Array<{ value: GameHistoryGame; label: string }>>([]);
  const [game, setGame] = useState("");
  const [status, setStatus] = useState("");
  const [result, setResult] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [selected, setSelected] = useState<GameHistoryItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => subscribeScoreInvalidations(() => setRevision((value) => value + 1)), []);

  useEffect(() => {
    let active = true;
    setSessions(null);
    setError(null);
    const parameters = new URLSearchParams({ page: String(page), page_size: "12" });
    if (game) parameters.set("game", game);
    if (status) parameters.set("status", status);
    if (result) parameters.set("result", result);
    if (dateFrom) parameters.set("date_from", dateFrom);
    if (dateTo) parameters.set("date_to", dateTo);
    void fetchGameHistory(parameters)
      .then((payload) => {
        if (!active) return;
        setSessions(payload.sessions);
        setGames(payload.filters.games);
        setPages(payload.pagination.pages);
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : "Historique indisponible.");
      });
    return () => { active = false; };
  }, [dateFrom, dateTo, game, page, result, revision, status]);

  const changeFilter = (setter: (value: string) => void, value: string) => {
    setPage(1);
    setSelected(null);
    setter(value);
  };

  return (
    <section className="collection-page history-page">
      <button className="back-button" type="button" onClick={() => navigate("/")}>← Retour au tableau de bord</button>
      <div className="collection-heading">
        <div>
          <p className="eyebrow">Progression personnelle</p>
          <h1>Mon historique</h1>
          <p>Retrouvez vos parties, reprenez une session interrompue ou rejouez une série.</p>
        </div>
        <span className="section-chip">Données personnelles</span>
      </div>
      <div className="history-filters" aria-label="Filtres de l’historique">
        <label>Mode<select value={game} onChange={(event) => changeFilter(setGame, event.target.value)}><option value="">Tous</option>{games.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
        <label>État<select value={status} onChange={(event) => changeFilter(setStatus, event.target.value)}><option value="">Tous</option><option value="active">En cours</option><option value="finished">Terminées</option><option value="abandoned">Abandonnées</option></select></label>
        <label>Résultat<select value={result} onChange={(event) => changeFilter(setResult, event.target.value)}><option value="">Tous</option><option value="won">Réussies</option><option value="lost">À retravailler</option></select></label>
        <label>Depuis<input type="date" value={dateFrom} onChange={(event) => changeFilter(setDateFrom, event.target.value)} /></label>
        <label>Jusqu’au<input type="date" value={dateTo} onChange={(event) => changeFilter(setDateTo, event.target.value)} /></label>
      </div>
      {error && <div className="info-notice">{error}</div>}
      {sessions === null && !error ? <div className="collection-loading">Chargement de votre historique…</div> : sessions?.length ? (
        <>
          <div className="history-list">
            {sessions.map((item) => (
              <article key={item.token} className="history-item">
                <div className="history-item-main">
                  <div className="collection-tile-top"><span className="subject-color" style={{ backgroundColor: item.subject?.color ?? "#5f63bb" }} /><span>{item.subject?.name ?? "Matière inconnue"}</span>{item.duel_id && <strong>Duel</strong>}</div>
                  <h2>{item.collection?.title ?? "Série supprimée"}</h2>
                  <p>{item.game_label}{item.difficulty_label ? ` · ${item.difficulty_label}` : ""} · {dateFormatter.format(new Date(item.created))}</p>
                </div>
                <div className="history-stats">
                  <span><strong>{item.result?.score ?? "—"}</strong> points</span>
                  <span><strong>{item.errors}</strong> erreur{item.errors > 1 ? "s" : ""}</span>
                  <span><strong>{item.result?.time ?? "—"}</strong> s</span>
                  <span className={`history-status ${item.status}`}>{item.status === "active" ? "En cours" : item.status === "abandoned" ? "Abandonnée" : item.result?.won ? "Réussie" : "Terminée"}</span>
                </div>
                <div className="history-actions">
                  <button className="button" type="button" onClick={() => setSelected(selected?.token === item.token ? null : item)}>Voir le détail</button>
                  {item.resume_path && <button className="button button-primary" type="button" onClick={() => navigate(item.resume_path!)}>Reprendre</button>}
                  {item.replay_path && <button className="button button-primary" type="button" onClick={() => navigate(item.replay_path!)}>Rejouer</button>}
                </div>
              </article>
            ))}
          </div>
          {selected && <HistoryDetail item={selected} onClose={() => setSelected(null)} />}
          {pages > 1 && <div className="history-pagination"><button className="button" type="button" disabled={page === 1} onClick={() => setPage((value) => value - 1)}>← Précédente</button><span>Page {page} sur {pages}</span><button className="button" type="button" disabled={page === pages} onClick={() => setPage((value) => value + 1)}>Suivante →</button></div>}
        </>
      ) : <div className="empty-state collection-empty"><strong>Aucune partie dans cet historique</strong><p>Une fois une partie commencée, elle apparaîtra ici automatiquement.</p></div>}
    </section>
  );
}
